// V-2629: Übergabe des Support-Tokens vom Admin-Fenster ans Kundencenter-Fenster per postMessage statt per Adresse.
// Admin (/app/admin) und Kundencenter (/app/my) laufen auf DERSELBEN Herkunft; ein Fremder kann weder
// `event.origin === origin` noch `event.source === <unser Fenster>` erfüllen. Das Token steht nie in einer URL.
// Reine Logik, ohne DOM — von tools/support-uebergabe.test.mjs geprüft.

export const UEBERGABE_PARAM = 'support_uebergabe';
export const NACHRICHT_BEREIT = 'support_bereit';
export const NACHRICHT_TOKEN = 'support_token';
export const UEBERGABE_FRIST_MS = 30_000;

const MAX_TOKEN_LAENGE = 4096;
const JWT_FORMAT = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

export interface UebergabeEreignis {
  origin: string;
  source: unknown;
  data: unknown;
}

function istVonErwarteterQuelle(ereignis: UebergabeEreignis, erwarteteQuelle: unknown, origin: string): boolean {
  if (!erwarteteQuelle) return false;
  if (ereignis.origin !== origin) return false;
  return ereignis.source === erwarteteQuelle;
}

function typVon(daten: unknown): { typ?: unknown; token?: unknown } | null {
  if (daten === null || typeof daten !== 'object') return null;
  return daten as { typ?: unknown; token?: unknown };
}

/** Nachricht „Kundencenter ist bereit" — nur vom erwarteten Fenster derselben Herkunft. */
export function istGueltigeBereitNachricht(ereignis: UebergabeEreignis, erwarteteQuelle: unknown, origin: string): boolean {
  if (!istVonErwarteterQuelle(ereignis, erwarteteQuelle, origin)) return false;
  return typVon(ereignis.data)?.typ === NACHRICHT_BEREIT;
}

/** Nachricht mit dem Support-Token — nur vom erwarteten Fenster derselben Herkunft, Token im JWT-Format. */
export function istGueltigeTokenNachricht(ereignis: UebergabeEreignis, erwarteteQuelle: unknown, origin: string): boolean {
  if (!istVonErwarteterQuelle(ereignis, erwarteteQuelle, origin)) return false;
  const daten = typVon(ereignis.data);
  if (!daten || daten.typ !== NACHRICHT_TOKEN) return false;
  const token = daten.token;
  return typeof token === 'string' && token.length <= MAX_TOKEN_LAENGE && JWT_FORMAT.test(token);
}

/** Läuft gerade eine Übergabe (Parameter gesetzt UND Admin-Fenster als Opener vorhanden)? Dann darf nichts die alte Sitzung zurückschreiben. */
export function uebergabeLaeuft(suche: string, opener: unknown): boolean {
  return new URLSearchParams(suche).get(UEBERGABE_PARAM) === '1' && !!opener;
}

/** Ein Hintergrund-Refresh darf nur schreiben, wenn das gespeicherte Token noch dasselbe ist wie beim Start des Aufrufs. */
export function darfRefreshSchreiben(tokenBeiStart: string | null | undefined, tokenJetzt: string | null | undefined): boolean {
  return !!tokenBeiStart && tokenBeiStart === tokenJetzt;
}

export interface SupportClaims {
  tenant_id: string;
  tenant_name?: string;
  email?: string;
  username?: string;
  super_admin_email?: string;
  super_admin_name?: string;
  session_id?: string;
  exp?: number;
}

function base64UrlDekodieren(teil: string): string {
  const base64 = teil.replace(/-/g, '+').replace(/_/g, '/');
  const aufgefuellt = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binaer = atob(aufgefuellt);
  const bytes = Uint8Array.from(binaer, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Liest nur Anzeige-Claims. Kein Vertrauen: Der Server prüft das JWT bei jedem Aufruf ohnehin. */
export function leseSupportClaims(token: string): SupportClaims | null {
  try {
    const teile = token.split('.');
    if (teile.length !== 3) return null;
    const payload = JSON.parse(base64UrlDekodieren(teile[1]));
    if (payload === null || typeof payload !== 'object') return null;
    if (typeof payload.tenant_id !== 'string' || payload.tenant_id === '') return null;
    return payload as SupportClaims;
  } catch {
    return null;
  }
}
